<template>
  <div class="review-record-dialog-root">
  <Modal
    v-model="recordModalOpen"
    class-name="review-record-dialog"
    :title="dialogTitle"
    width="980"
    :mask-closable="false"
    @on-cancel="handleClose"
  >
    <div class="review-record-body">
      <div ref="recordSummary" class="review-record-summary">
        <div class="review-record-summary-card review-record-summary-card--latest">
          <div class="review-record-summary-card-main">
            <div class="review-record-summary-label">最近送审版本</div>
            <div class="review-record-summary-value-row">
              <span class="review-record-summary-version">{{ latestVersionText }}</span>
            </div>
          </div>
          <span
            v-if="showLatestStatusTag"
            class="review-record-summary-status-tag review-record-status-tag"
            :class="`review-record-status-tag--${latestStatus}`"
          >
            {{ latestStatusText }}
          </span>
        </div>
        <div class="review-record-summary-card">
          <div class="review-record-summary-label">TT 当前线上版本</div>
          <div class="review-record-summary-value-row">
            <span class="review-record-summary-version">{{ onlineVersionText }}</span>
          </div>
        </div>
      </div>

      <div ref="recordSectionTitle" class="review-record-section-title">剧目审核记录</div>

      <Table
        ref="recordTable"
        class="review-record-table"
        :columns="columns"
        :data="displayRecordList"
        :loading="recordLoading"
        no-data-text="尚无送审记录"
      >
        <template #version="{ row }">
          <div class="review-record-version-cell">
            <span class="review-record-version">{{ row.version }}</span>
            <span
              v-if="row.isOnlineVersion"
              class="review-record-online-tag"
            >
              当前 TT 线上版本
            </span>
          </div>
        </template>
        <template #submitTime="{ row }">
          <div class="review-record-time">
            <div class="review-record-time-date">{{ row.submitTimeDate }}</div>
            <div v-if="row.submitTimeTime" class="review-record-time-clock">
              {{ row.submitTimeTime }}
            </div>
          </div>
        </template>
        <template #status="{ row }">
          <span
            class="review-record-status-tag"
            :class="`review-record-status-tag--${row.status}`"
          >
            {{ row.statusText }}
          </span>
        </template>
        <template #endTime="{ row }">
          <div class="review-record-time">
            <div class="review-record-time-date">{{ row.endTimeDate }}</div>
            <div v-if="row.endTimeTime" class="review-record-time-clock">
              {{ row.endTimeTime }}
            </div>
            <template v-if="row.showAppealFeedbackTime">
              <div class="review-record-appeal-feedback-label">申诉反馈：</div>
              <div class="review-record-appeal-feedback-date">
                {{ row.appealFeedbackDate }}
              </div>
              <div
                v-if="row.appealFeedbackTime"
                class="review-record-appeal-feedback-clock"
              >
                {{ row.appealFeedbackTime }}
              </div>
            </template>
          </div>
        </template>
        <template #result="{ row }">
          <div class="review-record-result-wrap">
            <div class="review-record-result">{{ row.resultDisplay }}</div>
            <div
              v-if="row.appealFeedbackResult"
              class="review-record-appeal-feedback-result"
            >
              {{ row.appealFeedbackResult }}
            </div>
          </div>
        </template>
        <template #action="{ row }">
          <div v-if="row.canWithdraw" class="review-record-actions">
            <span
              class="review-record-action"
              :class="{ 'review-record-action--disabled': withdrawSubmitting }"
              @click="handleWithdrawSubmit(row)"
            >
              {{ withdrawSubmitting ? '撤回中…' : '撤回送审' }}
            </span>
          </div>
          <div
            v-else-if="row.canModify || row.canAppeal || row.canViewAppealReason"
            class="review-record-actions"
          >
            <span
              v-if="row.canModify"
              class="review-record-action"
              :class="{
                'review-record-action--disabled':
                  actionsDisabled || row.modifyDisabled
              }"
              @click="handleModifySubmit(row)"
            >
              修改送审资料
            </span>
            <span
              v-if="row.canAppeal"
              class="review-record-action"
              :class="{
                'review-record-action--disabled':
                  actionsDisabled || row.appealDisabled
              }"
              @click="handleAppeal(row)"
            >
              发起申诉
            </span>
            <span
              v-if="row.canViewAppealReason"
              class="review-record-action"
              @click="handleViewAppealReason(row)"
            >
              查看申诉理由
            </span>
          </div>
        </template>
      </Table>

    </div>

    <template #footer>
      <Button @click="handleClose">关闭</Button>
    </template>
  </Modal>

  <AppealReasonViewDialog
    :visible.sync="appealReasonViewVisible"
    :row="row"
    :tt-version="appealViewTtVersion"
    @back-to-record="handleAppealReasonBackToRecord"
    @close="handleAppealReasonClose"
  />

  <Modal
    v-model="withdrawConfirmVisible"
    class-name="withdraw-submit-confirm-dialog"
    :title="withdrawConfirmTitle"
    width="980"
    :mask-closable="false"
    @on-cancel="handleWithdrawConfirmClose"
  >
    <p class="withdraw-submit-confirm-text">
      确认撤回 {{ withdrawConfirmVersion }}？该版本资料快照和审核记录会保留。撤回后不会自动再次送审；如有需重审的新资料，请在同步完成后确认。
    </p>
    <template #footer>
      <Button @click="handleWithdrawConfirmCancel">取消</Button>
      <Button
        class="withdraw-submit-confirm-btn"
        :loading="withdrawSubmitting"
        @click="handleWithdrawConfirmOk"
      >
        确认撤回
      </Button>
    </template>
  </Modal>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import {
  getTtMediaReviewVersions,
  postTtMediaReviewWithdraw
} from '@/api/short-review'
import { createTtRequestId } from '../../utils/resubmitPayload'
import AppealReasonViewDialog from '../AppealReasonViewDialog/index.vue'

/**
 * 审核记录展示规则（接口返回后仍走 normalizeRecordList）
 * - 表格按送审时间倒序；概览「最近送审版本」= 首行
 * - 结束时间：通过/驳回/已撤回展示；审核中/未送审为 —
 * - 结果说明：仅驳回展示；通过/审核中为 —
 * - 操作：审核中→撤回；最新一条驳回→修改/申诉；申诉中→修改+申诉（置灰）+查看理由；通过/已撤回/历史行→无
 */

const RECORD_STATUS_TEXT = {
  pending: '未送审',
  reviewing: '审核中',
  passed: '审核通过',
  rejected: '审核驳回',
  withdrawn: '已撤回',
  appealing: '已驳回-申诉中',
  appeal_passed: '审核通过（申诉通过）',
  appeal_rejected: '已驳回-申诉未通过'
}

/**
 * 申诉反馈展示（versions.items 字段，不依赖申诉详情接口）
 * - appealFinishedAt：申诉反馈时间 → 结束时间列「申诉反馈：」
 * - appealResultDescription：申诉结果说明 → 结果说明列「TT 申诉反馈：」
 */
function buildAppealFeedbackFields(item, isLatest) {
  const empty = {
    showAppealFeedbackTime: false,
    appealFeedbackDate: '',
    appealFeedbackTime: '',
    appealFeedbackResult: ''
  }
  if (!isLatest) return empty

  const feedbackAt = item.appealFinishedAt || ''
  const desc = (item.appealResultDescription || '').trim()
  if (!feedbackAt && !desc) return empty

  const feedbackParts = feedbackAt
    ? splitRecordTimeDisplay(feedbackAt)
    : { dateLine: '—', timeLine: '' }
  return {
    showAppealFeedbackTime: !!(feedbackAt && feedbackParts.dateLine !== '—'),
    appealFeedbackDate: feedbackParts.dateLine,
    appealFeedbackTime: feedbackParts.timeLine,
    appealFeedbackResult: desc ? `TT 申诉反馈：${desc}` : ''
  }
}

/** 接口 reviewStatus 0–7 → 表格 status */
function mapReviewStatusToKey(code) {
  const map = {
    0: 'pending',
    1: 'reviewing',
    2: 'passed',
    3: 'rejected',
    4: 'withdrawn',
    5: 'appealing',
    6: 'appeal_passed',
    7: 'appeal_rejected'
  }
  const n = Number(code)
  return map[n] || 'pending'
}

function formatTtVersionLabel(ttVersion) {
  const n = Number(ttVersion)
  if (ttVersion == null || ttVersion === '' || Number.isNaN(n) || n <= 0) {
    return '—'
  }
  return `V${n}`
}

/** versions 接口 items 项：保留文档字段，仅补充表格展示用字段 */
function mapVersionVoToRecordItem(vo, onlineTtVersion) {
  const tt = Number(vo.ttVersion)
  const online = Number(onlineTtVersion)
  return {
    ...vo,
    version: formatTtVersionLabel(vo.ttVersion),
    status: mapReviewStatusToKey(vo.reviewStatus),
    rawReviewStatus: Number(vo.reviewStatus),
    isOnlineVersion:
      !Number.isNaN(tt) &&
      tt > 0 &&
      !Number.isNaN(online) &&
      online > 0 &&
      tt === online
  }
}

function mapVersionsResponse(data) {
  if (!data) {
    return { items: [], onlineTtVersion: null, reviewTtVersion: null }
  }
  const onlineTtVersion = data.onlineTtVersion
  const reviewTtVersion = data.reviewTtVersion
  const items = (data.items || []).map((vo) =>
    mapVersionVoToRecordItem(vo, onlineTtVersion)
  )
  return {
    items,
    onlineTtVersion,
    reviewTtVersion
  }
}

/** 表格时间：第一行年月日，第二行时分 */
function splitRecordTimeDisplay(timeStr) {
  if (!timeStr || timeStr === '—') {
    return { dateLine: '—', timeLine: '' }
  }
  const parsed = dayjs(String(timeStr).replace(/\//g, '-'))
  if (!parsed.isValid()) {
    return { dateLine: String(timeStr), timeLine: '' }
  }
  return {
    dateLine: parsed.format('YYYY/MM/DD'),
    timeLine: parsed.format('HH:mm')
  }
}

const parseSubmitTime = (timeStr) => {
  if (!timeStr) return 0
  return new Date(String(timeStr).replace(/\//g, '-')).getTime()
}

function normalizeRecordList(list) {
  const sorted = [...(list || [])].sort(
    (a, b) =>
      parseSubmitTime(b.submittedAt) - parseSubmitTime(a.submittedAt)
  )
  return sorted.map((item, index) => {
    const isLatest = index === 0
    const status = item.status || 'pending'
    const statusText = item.statusText || RECORD_STATUS_TEXT[status] || '—'
    let endTimeParts = { dateLine: '—', timeLine: '' }
    if (
      status === 'passed' ||
      status === 'rejected' ||
      status === 'withdrawn' ||
      status === 'appealing' ||
      status === 'appeal_passed' ||
      status === 'appeal_rejected'
    ) {
      endTimeParts = splitRecordTimeDisplay(item.finishedAt)
    }
    const submitTimeParts = splitRecordTimeDisplay(item.submittedAt)
    let resultDisplay = '—'
    if (
      (status === 'rejected' ||
        status === 'appealing' ||
        status === 'appeal_passed' ||
        status === 'appeal_rejected') &&
      (item.resultDescription || '').trim()
    ) {
      resultDisplay = item.resultDescription.trim()
    } else if (status === 'withdrawn') {
      resultDisplay = (item.resultDescription || '').trim() || '主动撤回'
    }
    const isAppealingLatest = isLatest && status === 'appealing'
    const canViewAppealReason =
      isLatest &&
      (status === 'appeal_rejected' ||
        status === 'appeal_passed' ||
        status === 'appealing')
    const appealFeedbackFields = buildAppealFeedbackFields(item, isLatest)
    return {
      ...item,
      status,
      statusText,
      submitTimeDate: submitTimeParts.dateLine,
      submitTimeTime: submitTimeParts.timeLine,
      endTimeDate: endTimeParts.dateLine,
      endTimeTime: endTimeParts.timeLine,
      resultDisplay,
      ...appealFeedbackFields,
      canWithdraw: !!item.canWithdraw,
      // 申诉中仍展示「修改送审资料」，与「发起申诉」一并置灰，仅「查看申诉理由」可点
      canModify: !!item.canResubmit || isAppealingLatest,
      modifyDisabled: isAppealingLatest,
      canAppeal:
        isLatest &&
        (status === 'rejected' ||
          status === 'appealing' ||
          status === 'appeal_rejected'),
      appealDisabled:
        isAppealingLatest ||
        status === 'appeal_rejected' ||
        (status === 'rejected' && !item.canAppeal),
      canViewAppealReason
    }
  })
}

export default {
  name: 'ReviewRecordDialog',
  components: {
    AppealReasonViewDialog
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    row: {
      type: Object,
      default: () => ({})
    },
    /** 已执行修改送审或申诉后，操作入口置灰 */
    actionsDisabled: {
      type: Boolean,
      default: false
    },
    /** 列表态是否允许发起申诉（仅审核明细 3） */
    appealAllowed: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      appealReasonViewVisible: false,
      appealViewTtVersion: null,
      recordModalOpen: false,
      withdrawConfirmVisible: false,
      pendingWithdrawRow: null,
      recordList: [],
      recordLoading: false,
      _versionsFetching: false,
      _versionsFetchId: null,
      versionsMeta: {
        onlineTtVersion: null,
        reviewTtVersion: null
      },
      withdrawSubmitting: false,
      _recordTableResizeObserver: null,
      _recordModalHeightTimer: null,
      columns: [
        { title: '送审版本', slot: 'version', width: 180 },
        { title: '送审时间', slot: 'submitTime', width: 117 },
        {
          title: '审核状态',
          slot: 'status',
          width: 162,
          className: 'review-record-col-status'
        },
        {
          title: '结束时间',
          slot: 'endTime',
          width: 117,
          className: 'review-record-col-end-time'
        },
        {
          title: '结果说明',
          slot: 'result',
          className: 'review-record-col-result'
        },
        {
          title: '操作',
          slot: 'action',
          width: 138,
          className: 'review-record-col-action'
        }
      ]
    }
  },
  computed: {
    dialogTitle() {
      const name = (this.row && this.row.playName) || '—'
      return `审核记录 · ${name}`
    },
    displayRecordList() {
      return normalizeRecordList(this.recordList)
    },
    latestRecord() {
      return this.displayRecordList[0] || null
    },
    latestVersionText() {
      const fromMeta = formatTtVersionLabel(this.versionsMeta.reviewTtVersion)
      if (fromMeta !== '—') return fromMeta
      return (this.latestRecord && this.latestRecord.version) || '暂无'
    },
    latestStatus() {
      return (this.latestRecord && this.latestRecord.status) || ''
    },
    latestStatusText() {
      return (this.latestRecord && this.latestRecord.statusText) || ''
    },
    /** 概览区状态标签与「剧目审核记录」表格首行（最近送审）一致 */
    showLatestStatusTag() {
      const latest = this.latestRecord
      if (!latest || !latest.status) return false
      return !!(latest.statusText && latest.statusText !== '—')
    },
    withdrawConfirmTitle() {
      const name = (this.row && this.row.playName) || '—'
      return `撤回送审 · ${name}`
    },
    withdrawConfirmVersion() {
      return (this.pendingWithdrawRow && this.pendingWithdrawRow.version) || '—'
    },
    onlineVersionText() {
      const fromMeta = formatTtVersionLabel(this.versionsMeta.onlineTtVersion)
      if (fromMeta !== '—') return fromMeta
      const online = this.recordList.find((item) => item.isOnlineVersion)
      return (online && online.version) || '暂无'
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.withdrawSubmitting = false
        this.pendingWithdrawRow = null
        this.withdrawConfirmVisible = false
        this.appealReasonViewVisible = false
        this.recordModalOpen = true
        this.$nextTick(() => {
          if (this.visible) {
            this.loadReviewVersions()
          }
        })
      } else {
        this.recordModalOpen = false
        this.withdrawConfirmVisible = false
        this.pendingWithdrawRow = null
        this.appealReasonViewVisible = false
        this.resetVersionsData()
      }
    },
    'row.id'(val, oldVal) {
      if (!this.recordModalOpen || val == null) return
      // 首开时 row 与 visible 同 tick 更新，由 visible 统一拉取
      if (oldVal == null || oldVal === '') return
      if (val === oldVal) return
      this.loadReviewVersions()
    },
    'row.reviewStatusApi'(val, oldVal) {
      if (!this.recordModalOpen) return
      if (oldVal == null || oldVal === '') return
      if (val === oldVal) return
      this.loadReviewVersions()
    },
    recordModalOpen(val) {
      if (val) {
        this.$nextTick(() => {
          this.bindRecordModalHeightSync()
        })
      } else {
        this.unbindRecordModalHeightSync()
      }
    },
    displayRecordList: {
      handler() {
        if (!this.recordModalOpen) return
        this.scheduleRecordModalHeightSync()
      },
      deep: true
    }
  },
  beforeDestroy() {
    this.unbindRecordModalHeightSync()
  },
  methods: {
    resetVersionsData() {
      this.recordList = []
      this.appealViewTtVersion = null
      this.versionsMeta = {
        onlineTtVersion: null,
        reviewTtVersion: null
      }
      this.recordLoading = false
    },
    loadReviewVersions() {
      const id = this.row && this.row.id
      if (id == null || id === '') {
        this.resetVersionsData()
        return
      }
      if (this._versionsFetching && this._versionsFetchId === id) {
        return
      }
      this._versionsFetching = true
      this._versionsFetchId = id
      this.recordLoading = true
      getTtMediaReviewVersions(id)
        .then((res) => {
          if (res.code === 200 && res.data) {
            const mapped = mapVersionsResponse(res.data)
            this.recordList = mapped.items
            this.versionsMeta = {
              onlineTtVersion: mapped.onlineTtVersion,
              reviewTtVersion: mapped.reviewTtVersion
            }
            return
          }
          this.resetVersionsData()
          this.$Message.error(res.message || '获取审核记录失败')
        })
        .catch(() => {
          this.resetVersionsData()
          this.$Message.error('获取审核记录失败')
        })
        .finally(() => {
          this.recordLoading = false
          this._versionsFetching = false
          this.scheduleRecordModalHeightSync()
        })
    },
    getRecordModalBodyEl() {
      const wraps = document.querySelectorAll('.ivu-modal-wrap.review-record-dialog')
      for (let i = 0; i < wraps.length; i += 1) {
        const wrap = wraps[i]
        if (wrap.style.display === 'none') continue
        return wrap.querySelector('.ivu-modal-body')
      }
      return null
    },
    getRecordTableWrapperEl() {
      const table = this.$refs.recordTable
      if (!table || !table.$el) return null
      return table.$el.querySelector('.ivu-table-wrapper')
    },
    /** 弹窗 body 高度 = 概览区 + 标题 + 表格实际高度 + body 内边距 */
    calcRecordModalBodyMinHeight() {
      const wrapper = this.getRecordTableWrapperEl()
      if (!wrapper) return null
      const tableHeight = wrapper.offsetHeight
      if (!tableHeight) return null

      const summaryEl = this.$refs.recordSummary
      const titleEl = this.$refs.recordSectionTitle
      const summaryH = summaryEl ? summaryEl.offsetHeight : 0
      const titleH = titleEl ? titleEl.offsetHeight : 0
      const summaryMarginBottom = 20
      const titleMarginBottom = 12
      const bodyPaddingY = 20 + 32

      return (
        summaryH +
        summaryMarginBottom +
        titleH +
        titleMarginBottom +
        tableHeight +
        bodyPaddingY
      )
    },
    syncRecordModalBodyHeight() {
      if (!this.recordModalOpen) return
      const body = this.getRecordModalBodyEl()
      if (!body) return
      const minHeight = this.calcRecordModalBodyMinHeight()
      if (minHeight == null) {
        body.style.removeProperty('min-height')
        return
      }
      body.style.minHeight = `${minHeight}px`
    },
    scheduleRecordModalHeightSync(delay = 80) {
      if (!this.recordModalOpen) return
      clearTimeout(this._recordModalHeightTimer)
      this._recordModalHeightTimer = setTimeout(() => {
        this.$nextTick(() => {
          requestAnimationFrame(() => this.syncRecordModalBodyHeight())
        })
      }, delay)
    },
    bindRecordModalHeightSync() {
      this.unbindRecordModalHeightSync()
      const wrapper = this.getRecordTableWrapperEl()
      if (typeof ResizeObserver !== 'undefined' && wrapper) {
        this._recordTableResizeObserver = new ResizeObserver(() => {
          this.scheduleRecordModalHeightSync(60)
        })
        this._recordTableResizeObserver.observe(wrapper)
      }
      this.scheduleRecordModalHeightSync(120)
    },
    unbindRecordModalHeightSync() {
      if (this._recordTableResizeObserver) {
        this._recordTableResizeObserver.disconnect()
        this._recordTableResizeObserver = null
      }
      clearTimeout(this._recordModalHeightTimer)
      const body = this.getRecordModalBodyEl()
      if (body) body.style.removeProperty('min-height')
    },
    handleClose() {
      this.recordModalOpen = false
      this.withdrawConfirmVisible = false
      this.pendingWithdrawRow = null
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    handleModifySubmit(row) {
      if (this.actionsDisabled || !row.canModify || row.modifyDisabled) {
        if (row.modifyDisabled) {
          this.$Message.info('申诉中不可修改送审资料')
        }
        return
      }
      this.$emit('modify-submit', {
        ...this.row,
        rejectVersion: row.version,
        rejectReason: row.resultDescription || row.resultDisplay
      })
      this.recordModalOpen = false
      this.$emit('update:visible', false)
    },
    handleAppeal(row) {
      if (this.actionsDisabled || !row.canAppeal || row.appealDisabled) {
        if (row.appealDisabled && row.status === 'appealing') {
          this.$Message.info('已发起申诉，请等待 TT 反馈')
        } else if (
          row.appealDisabled &&
          (row.status === 'appeal_rejected' ||
            Number(this.row && this.row.reviewStatusApi) === 7)
        ) {
          this.$Message.info('申诉未通过，不支持二次申诉')
        }
        return
      }
      this.recordModalOpen = false
      this.$emit('update:visible', false)
      this.$nextTick(() => {
        this.$emit('open-appeal', {
          ...this.row,
          rejectVersion: row.version,
          ttVersion: row.ttVersion != null ? row.ttVersion : row.version,
          rejectReason: row.resultDescription || row.resultDisplay
        })
      })
    },
    handleViewAppealReason(row) {
      const tt = row && row.ttVersion
      this.appealViewTtVersion =
        tt != null && tt !== '' ? tt : this.versionsMeta.reviewTtVersion
      this.recordModalOpen = false
      this.$nextTick(() => {
        this.appealReasonViewVisible = true
      })
    },
    /** 关闭申诉理由弹窗并退出审核记录流程 */
    handleAppealReasonClose() {
      this.appealReasonViewVisible = false
      this.recordModalOpen = false
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    /** 关闭申诉理由弹窗并回到审核记录 */
    handleAppealReasonBackToRecord() {
      this.appealReasonViewVisible = false
      this.recordModalOpen = true
      this.$nextTick(() => {
        this.bindRecordModalHeightSync()
      })
    },
    handleWithdrawSubmit(row) {
      if (this.withdrawSubmitting || !row.canWithdraw) return
      this.pendingWithdrawRow = row
      this.recordModalOpen = false
      this.$nextTick(() => {
        this.withdrawConfirmVisible = true
      })
    },
    /** 右上角关闭：仅关闭撤回确认，不回到审核记录 */
    handleWithdrawConfirmClose() {
      this.withdrawConfirmVisible = false
      this.pendingWithdrawRow = null
      this.recordModalOpen = false
      this.$emit('update:visible', false)
    },
    /** 底部「取消」：关闭撤回确认并回到审核记录 */
    handleWithdrawConfirmCancel() {
      this.withdrawConfirmVisible = false
      this.pendingWithdrawRow = null
      this.$nextTick(() => {
        this.recordModalOpen = true
        this.bindRecordModalHeightSync()
      })
    },
    handleWithdrawConfirmOk() {
      const row = this.pendingWithdrawRow
      if (this.withdrawSubmitting || !row) return
      const id = this.row && this.row.id
      if (id == null || id === '') {
        this.$Message.error('缺少审核记录，无法撤回')
        return
      }
      const ttVersion = Number(row.ttVersion)
      if (Number.isNaN(ttVersion) || ttVersion <= 0) {
        this.$Message.error('缺少送审版本号，无法撤回')
        return
      }
      this.withdrawSubmitting = true
      postTtMediaReviewWithdraw(id, {
        requestId: createTtRequestId('withdraw'),
        ttVersion
      })
        .then((res) => {
          if (res.code === 200) {
            const taskId =
              res.data && res.data.taskId ? String(res.data.taskId) : ''
            this.$Message.success(
              taskId
                ? `撤回已受理，任务 ID：${taskId}`
                : res.message || '已撤回送审'
            )
            this.$emit('withdraw-success', { ...this.row, version: row.version })
            this.withdrawConfirmVisible = false
            this.pendingWithdrawRow = null
            this.$nextTick(() => {
              this.recordModalOpen = true
              this.loadReviewVersions()
            })
            return
          }
          this.$Message.error(res.message || '撤回送审失败')
        })
        .catch(() => {
          this.$Message.error('撤回送审失败')
        })
        .finally(() => {
          this.withdrawSubmitting = false
        })
    }
  }
}
</script>

<style scoped>
.review-record-body {
  padding: 0 2px;
  font-size: 12px;
  line-height: 24px;
  color: #303133;
}

.review-record-summary {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
}

.review-record-summary-card {
  flex: 1;
  min-width: 0;
  padding: 16px 20px;
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.review-record-summary-card--latest {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.review-record-summary-card-main {
  flex: 1;
  min-width: 0;
}

.review-record-summary-status-tag {
  flex-shrink: 0;
  align-self: center;
}

.review-record-summary-label {
  font-size: 13px;
  line-height: 22px;
  color: #909399;
}

.review-record-summary-value-row {
  display: flex;
  align-items: center;
}

.review-record-summary-version {
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #303133;
}

.review-record-section-title {
  margin-bottom: 12px;
  font-size: 14px;
  line-height: 22px;
  font-weight: 700;
  color: #303133;
}

/* 表格宽度随弹窗内容区 100%，避免固定 976px 超出 modal 被裁切 */
.review-record-table {
  width: 100%;
}

.review-record-table >>> .ivu-table-wrapper,
.review-record-table >>> .ivu-table-overflowX {
  overflow-x: hidden;
}

.review-record-table >>> .ivu-table-header table,
.review-record-table >>> .ivu-table-body table {
  width: 100% !important;
  table-layout: fixed;
}

.review-record-table >>> .ivu-table-body {
  overflow-x: hidden !important;
}

.review-record-table >>> .ivu-table-body .ivu-table-cell {
  overflow: visible;
}

.review-record-table >>> .ivu-table-tip td {
  font-size: 13px;
  color: #909399;
}

.review-record-table >>> .ivu-table th {
  background: #fafafa;
  height: 52px;
}

.review-record-table >>> .ivu-table-header .ivu-table-cell {
  font-size: 12px;
  font-weight: 700;
  height: 52px;
  line-height: 52px;
  padding-top: 0;
  padding-bottom: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.review-record-table >>> .ivu-table-body .ivu-table-cell {
  font-size: 13px;
  line-height: 20px;
  min-height: 52px;
  padding: 16px 18px;
  box-sizing: border-box;
}

.review-record-table >>> .ivu-table-body tr td {
  height: auto;
  min-height: 52px;
  vertical-align: middle;
}

.review-record-table >>> td.review-record-col-result .ivu-table-cell {
  height: auto;
  min-height: 52px;
  padding-top: 16px;
  padding-bottom: 16px;
  white-space: normal;
  word-break: break-word;
}

.review-record-table >>> th.review-record-col-status,
.review-record-table >>> td.review-record-col-status .ivu-table-cell {
  text-align: left;
  padding-right: 20px;
}

.review-record-table >>> th.review-record-col-end-time,
.review-record-table >>> td.review-record-col-end-time .ivu-table-cell {
  padding-left: 8px;
}

.review-record-table >>> th.review-record-col-action,
.review-record-table >>> td.review-record-col-action .ivu-table-cell {
  overflow: visible;
  padding-right: 8px;
}

.review-record-time {
  display: inline-block;
  font-size: 13px;
  line-height: 20px;
  color: #303133;
}

.review-record-time-date,
.review-record-time-clock {
  white-space: nowrap;
}

.review-record-version-cell {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.review-record-version {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 24px;
  color: #2d8cf0;
  white-space: nowrap;
}

.review-record-online-tag {
  flex-shrink: 0;
  display: inline-block;
  padding: 0 8px;
  font-size: 13px;
  line-height: 24px;
  color: #67c23a;
  background: #f0f9eb;
  border: 1px solid #c2e7b0;
  border-radius: 2px;
  white-space: nowrap;
}

.review-record-status-tag {
  display: inline-block;
  padding: 0 8px;
  font-size: 12px;
  line-height: 24px;
  border-radius: 4px;
  border: 1px solid transparent;
  white-space: nowrap;
}

.review-record-status-tag--pending {
  color: #909399;
  background: #f4f4f5;
  border-color: #e4e7ed;
}

.review-record-status-tag--rejected,
.review-record-status-tag--appealing,
.review-record-status-tag--appeal_rejected {
  color: #de5a50;
  background: #fef0f0;
  border-color: #de5a50;
}

.review-record-status-tag--reviewing {
  color: #409eff;
  background: #ecf5ff;
  border-color: #b3d8ff;
}

.review-record-status-tag--passed,
.review-record-status-tag--appeal_passed {
  color: #67c23a;
  background: #f0f9eb;
  border-color: #c2e7b0;
}

.review-record-status-tag--withdrawn {
  color: #909399;
  background: #f4f4f5;
  border-color: #e4e7ed;
}

.review-record-result-wrap {
  min-width: 0;
}

.review-record-result {
  font-size: 13px;
  line-height: 20px;
  color: #606266;
  word-break: break-word;
  white-space: normal;
}

.review-record-appeal-feedback-result {
  margin-top: 6px;
  font-size: 12px;
  line-height: 18px;
  color: #909399;
  word-break: break-word;
}

.review-record-appeal-feedback-label {
  margin-top: 8px;
  font-size: 12px;
  line-height: 18px;
  color: #909399;
}

.review-record-appeal-feedback-date,
.review-record-appeal-feedback-clock {
  font-size: 12px;
  line-height: 18px;
  color: #909399;
  white-space: nowrap;
}

.review-record-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  gap: 8px;
}

.review-record-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.review-record-action {
  display: inline-block;
  font-size: 13px;
  line-height: 20px;
  color: #2d8cf0;
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
}

.review-record-action:hover {
  color: #57a3f3;
}

/* 禁用时仍为蓝色操作样式，仅变淡，不改成灰色文字 */
.review-record-action--disabled,
.review-record-action--disabled:hover {
  color: #2d8cf0;
  opacity: 0.35;
  cursor: not-allowed;
  pointer-events: none;
}
</style>

<style>
.review-record-dialog .ivu-modal-header {
  box-sizing: border-box;
  padding: 16px 24px;
  border-bottom: 1px solid #e8eaec;
  display: flex;
  align-items: center;
}

.review-record-dialog .ivu-modal-header-inner {
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  padding-right: 48px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.review-record-dialog .ivu-modal-close {
  top: 16px;
  right: 20px;
  z-index: 1;
}

.review-record-dialog .ivu-modal-body {
  height: auto;
  min-height: 0;
  padding: 20px 24px 32px;
  overflow-x: hidden;
  overflow-y: visible;
  box-sizing: border-box;
}

.review-record-dialog .ivu-modal-footer {
  padding: 12px 24px 16px;
  border-top: 1px solid #e8eaec;
}

.review-record-dialog .ivu-modal-footer .ivu-btn {
  font-size: 12px;
  line-height: 24px;
}

/* View Design：class-name 挂在 .ivu-modal-wrap 上，flex 实现垂直水平居中 */
.withdraw-submit-confirm-dialog {
  display: flex;
  align-items: center;
  justify-content: center;
}

.withdraw-submit-confirm-dialog .ivu-modal {
  top: 0;
}

.withdraw-submit-confirm-dialog .ivu-modal-content {
  height: 230px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.withdraw-submit-confirm-dialog .ivu-modal-header {
  flex-shrink: 0;
  height: 76px;
  box-sizing: border-box;
  padding: 0 24px;
  border-bottom: 1px solid #e8eaec;
  display: flex;
  align-items: center;
}

.withdraw-submit-confirm-dialog .ivu-modal-header-inner {
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  padding-right: 48px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  color: #263f58;
}

.withdraw-submit-confirm-dialog .ivu-modal-close {
  top: 22px;
  right: 20px;
  z-index: 1;
}

.withdraw-submit-confirm-dialog .ivu-modal-body {
  flex-shrink: 0;
  height: 87px;
  box-sizing: border-box;
  padding: 0 24px;
  overflow: hidden;
}

.withdraw-submit-confirm-text {
  margin: 13px 0;
  font-size: 13px;
  line-height: 17px;
  color: #40536b;
}

.withdraw-submit-confirm-dialog .ivu-modal-footer {
  flex-shrink: 0;
  height: 66px;
  box-sizing: border-box;
  padding: 0 24px;
  border-top: 1px solid #e8eaec;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.withdraw-submit-confirm-dialog .ivu-modal-footer .ivu-btn {
  height: 35px;
  padding: 8px 15px;
  font-size: 13px;
  line-height: 17px;
}

.withdraw-submit-confirm-dialog .withdraw-submit-confirm-btn {
  color: #c23d32;
  background: #fff7f6;
  border-color: #c23d32;
}

.withdraw-submit-confirm-dialog .withdraw-submit-confirm-btn:hover {
  color: #c23d32;
  border-color: #c23d32;
  background: #fff0ee;
}
</style>
